import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseCircleFilled,
  EditOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import {
  Collapse,
  CollapseProps,
  Input,
  InputNumber,
  Space,
  Switch,
  Typography,
} from 'antd';
import { StyledCard } from 'components/Custom/card';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { HmrcConnectButton } from 'components/OrganisationDetails/HmrcConnectButton';
// import { HmrcTestHeaderButton } from 'components/OrganisationDetails/HmrcTestHeaderButton';
import { sortVatReturnLogs } from 'utils/sort-vat-return-logs';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { MessageStates } from 'containers/MessageBox';
import moment from 'moment';
import {
  HIDE_CANCELLED_ENTITY,
  PAGE_SIZE_ENTITY,
} from 'pages/organisations/organisation';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { getReturnTypeNameLabel } from 'i18n/returnTypeLabel';
import { formatDisplayDate, formatDisplayDateRange } from 'i18n/calendarLabels';
import { useDispatch, useSelector } from 'react-redux';
import { AccountType, Provider } from 'store/api/enums';
import { getReturnFrequencyLabel } from 'i18n/returnFrequencyLabel';
import { turnMessageOn } from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  DataLogStatus,
  TaxReportCountry,
} from 'store/app/enums';
import { User } from 'store/auth/models';
import {
  fetchAddUrlRequest,
  // hmrcFraudHeaderTestRequest,
} from 'store/organisation/actions';
// import { getOrgLoading } from 'store/organisation/selectors';
import { Mapping, Organisation } from 'store/organisation/models';
import { updateReportFieldsRequest } from 'store/vat/actions';
import { VatReturnLog } from 'store/vat/models';
import styled, { CSSProperties } from 'styled-components';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { findGroupOrganisationForMember } from 'utils/find-group-for-entity';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { useNumberFormat } from 'utils/numberFormat';
import { Preference, PreferenceActions } from 'utils/preferences';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import {
  getReturnActionLabel,
  isReturnActionPrimary,
  RETURN_ACTION_BUTTON_WIDTH_ENTITY,
  toReturnActionRecord,
} from 'utils/return-action-label';
import { StatusGetter } from 'utils/status-getter';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import { CardInfo } from './card-info';

/** In-session draft only; no local persistence until BE saves these fields. */
type ReturnLineDraft = { reference: string; returnTax: string };

/** Prefix already locale-formatted amount with currency symbol (e.g. £ 1,234.56). */
function formatReturnTaxDisplayWithCurrency(
  currencyCode: string | undefined | null,
  localeForIntl: string,
  formattedNumber: string
): string {
  const code = currencyCode?.trim();
  if (!code) return formattedNumber;
  const upper = code.toUpperCase();
  if (upper.length !== 3) return formattedNumber;
  try {
    const parts = new Intl.NumberFormat(localeForIntl, {
      style: 'currency',
      currency: upper,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const symbol = parts.find((p) => p.type === 'currency')?.value?.trim();
    if (!symbol) return formattedNumber;
    return `${symbol} ${formattedNumber}`;
  } catch {
    return `${upper} ${formattedNumber}`;
  }
}

function isUkVatReturnMapping(
  countryName: string | undefined,
  mappingName: string | undefined
): boolean {
  const country = (countryName ?? '').trim().toLowerCase();
  const isUkCountry =
    country === TaxReportCountry.UK.toLowerCase() ||
    country === 'united kingdom' ||
    country === 'uk';
  const name = (mappingName ?? '').trim().toLowerCase();
  const isUkVatReturn = name.includes('uk vat return');
  return isUkCountry && isUkVatReturn;
}

interface MappingDetailsProps {
  mapping: Mapping;
  countryName: string | undefined;
  connectionId: string | undefined;
  vatReturnLogs: VatReturnLog[];
  vatGenerate: (
    connectionId: string,
    mappingId: string,
    dataLogId?: string
  ) => void;
  editReturnMapping: (connectionId: string, mappingId: string) => void;
  connected: boolean;
  currentUser?: User;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  organisation?: any; // Entity or Organisation type for operation rights
  hasAdvancedUserRights: boolean;
  updateReportFields: (
    dataLogId: string,
    reference: string,
    returnTax: string
  ) => void;
  allOrganisations?: Organisation[];
  allMappings?: Mapping[];
  /**
   * When true, render only the submissions table (no accordion header / action
   * buttons). Used by the group overview, where the heade@monorepo/shared-lib live in the
   * GroupSummaryCard so we just want the table beneath it.
   */
  bodyOnly?: boolean;
}

const { Text } = Typography;

/** Match antd small InputNumber control height (size="small"). */
const REF_RETURN_INPUT_HEIGHT_PX = 24;

/** Edit pencil icon in reference / return-tax cells (2px larger than previous 11px). */
const CELL_EDIT_PENCIL_PX = 13;
const CELL_EDIT_PENCIL_COLOR = 'var(--color-primary, #41aaaf)';
const REFERENCE_MAX_LENGTH = 30;
const RETURN_TAX_MAX = 100000000000;
const RETURN_TAX_MIN = -RETURN_TAX_MAX;

/** Same column/input footprint as return tax (narrow field). */
const ReferenceInputWrap = styled.div`
  width: 135px;
  max-width: none;
  min-height: ${REF_RETURN_INPUT_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  & .ant-input {
    width: 100%;
    height: ${REF_RETURN_INPUT_HEIGHT_PX}px;
    line-height: 22px;
    box-sizing: border-box;
  }
`;

const ReturnTaxNumberWrap = styled.div`
  width: 100%;
  max-width: 100px;
  margin-left: auto;
  min-height: ${REF_RETURN_INPUT_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  position: relative;
  & .ant-input-number {
    width: 100%;
    height: ${REF_RETURN_INPUT_HEIGHT_PX}px;
    box-sizing: border-box;
  }
  & .ant-input-number-input-wrap {
    width: 100%;
  }
  & .ant-input-number-input {
    height: ${REF_RETURN_INPUT_HEIGHT_PX}px;
    line-height: 22px;
    text-align: right;
    box-sizing: border-box;
    padding-right: 28px !important;
  }
`;

export const MappingDetails: React.FC<MappingDetailsProps> = ({
  mapping,
  countryName,
  connectionId,
  vatReturnLogs,
  vatGenerate,
  editReturnMapping,
  connected,
  currentUser,
  handlePreferences,
  organisation,
  hasAdvancedUserRights,
  updateReportFields,
  allOrganisations,
  allMappings,
  bodyOnly,
}) => {
  const dispatch = useDispatch();
  // const orgLoading = useSelector(getOrgLoading);
  const { t, i18n } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const { localizeCountry } = useCountryLabel();
  const { formatNumber, locale: numberFormatLocale } = useNumberFormat();
  const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_ENTITY + '-' + mapping.mappingId,
  });
  const hideCancelledSaved = handlePreferences(PreferenceActions.GET, {
    name: HIDE_CANCELLED_ENTITY + '-' + mapping.mappingId,
  });

  const [hideCancelled, setHideCancelled] = React.useState(hideCancelledSaved);
  const [pwaSubmissionsPage, setPwaSubmissionsPage] = React.useState(1);
  const [lineDraftById, setLineDraftById] = React.useState<
    Record<string, ReturnLineDraft>
  >({});

  React.useEffect(() => {
    setPwaSubmissionsPage(1);
  }, [mapping.mappingId, hideCancelled, vatReturnLogs]);

  const setLineField = React.useCallback(
    (dataLogUuid: string, field: keyof ReturnLineDraft, value: string) => {
      setLineDraftById((prev) => {
        // Important: do NOT default the sibling field to ''.
        // Otherwise editing Reference would clear Return Tax (and vice-versa)
        // before the user actually changes that field.
        const editingCur =
          editingLineCurrentRef.current?.uuid === dataLogUuid
            ? editingLineCurrentRef.current
            : null;
        const cur =
          prev[dataLogUuid] ??
          (editingCur
            ? {
                reference: editingCur.reference,
                returnTax: editingCur.returnTax,
              }
            : { reference: '', returnTax: '' });
        return {
          ...prev,
          [dataLogUuid]: { ...cur, [field]: value },
        };
      });
    },
    []
  );

  const [editingLineField, setEditingLineField] = React.useState<{
    uuid: string;
    field: 'reference' | 'returnTax';
  } | null>(null);
  const editingLineInitialRef = React.useRef<{
    uuid: string;
    reference: string;
    returnTax: string;
  } | null>(null);
  const editingLineCurrentRef = React.useRef<{
    uuid: string;
    reference: string;
    returnTax: string;
  } | null>(null);
  const cancelEditOnBlurRef = React.useRef<string | null>(null);
  const submitWithEnterRef = React.useRef<{
    uuid: string;
    field: 'reference' | 'returnTax';
  } | null>(null);

  React.useEffect(() => {
    setEditingLineField(null);
    editingLineInitialRef.current = null;
    editingLineCurrentRef.current = null;
    cancelEditOnBlurRef.current = null;
    submitWithEnterRef.current = null;
  }, [mapping.mappingId]);

  const showHmrcConnected = Boolean(
    (organisation?.hmrcConnectionUuid ?? '').trim()
  );
  const hasParentGroupStartedSubmission = React.useMemo(() => {
    const isCurrentGroup =
      organisation?.providerName?.toLowerCase() ===
      Provider.GROUP.toLowerCase();
    if (isCurrentGroup) return false;
    const parentGroup = findGroupOrganisationForMember(
      allOrganisations,
      organisation?.uuid
    );
    if (!parentGroup?.uuid || !mapping?.returnTypeId) return false;
    const parentGroupMappings = (allMappings || []).filter(
      (m) =>
        m.organisationId === parentGroup.uuid &&
        m.returnTypeId === mapping.returnTypeId
    );
    if (parentGroupMappings.length === 0) return false;

    // Use mapping-owned dataLogs instead of page-scoped vatReturnLogs.
    // On single-entity pages, vatReturnLogs may not include parent group logs.
    return parentGroupMappings.some((groupMapping) =>
      (groupMapping.dataLogs || []).some((log) => {
        const status = DataLogStatus[log.status] || (log.status as string);
        return (
          status !== DataLogStatus.CANCELLED &&
          status !== DataLogStatus.SUBMITTED
        );
      })
    );
  }, [
    allMappings,
    allOrganisations,
    mapping?.returnTypeId,
    organisation?.providerName,
    organisation?.uuid,
  ]);

  const getVatLogsPerMapping = () => {
    let logs =
      (currentUser &&
        AccountType[currentUser.accountType] === AccountType.CLIENT) ||
      hideCancelled
        ? vatReturnLogs?.filter(
            (vat) =>
              vat.mappingId === mapping.mappingId &&
              DataLogStatus[vat.status] !== DataLogStatus.CANCELLED
          )
        : vatReturnLogs?.filter(
            (vat) => vat.mappingId === mapping.mappingId
          );

    if (pwaMobileNav) {
      logs = logs?.filter(
        (vat) => DataLogStatus[vat.status] === DataLogStatus.SUBMITTED
      );
    }

    return logs;
  };

  const getTableRows = (): VatReturnLog[] => {
    const logs = getVatLogsPerMapping() || [];
    const dataLogByUuid = new Map(
      (mapping.dataLogs || []).map((dataLog) => [dataLog.uuid, dataLog])
    );

    return sortVatReturnLogs(logs).map((log) => {
      const mappingLog = dataLogByUuid.get(log.uuid);
      return {
        ...log,
        preparerUser: log.preparerUser ?? mappingLog?.preparerUser ?? null,
        savedByUserFullName:
          log.savedByUserFullName ?? mappingLog?.savedByUserFullName ?? null,
        submittedByUser:
          log.submittedByUser ?? mappingLog?.submittedByUser ?? null,
        submittedBy: log.submittedBy ?? mappingLog?.submittedBy ?? null,
        reference: lineDraftById[log.uuid]?.reference ?? log.reference ?? '',
        returnTax: lineDraftById[log.uuid]?.returnTax ?? log.returnTax ?? '',
      };
    });
  };
  const originalLineById = React.useMemo(() => {
    const logs = getVatLogsPerMapping() || [];
    return logs.reduce<Record<string, ReturnLineDraft>>((acc, log) => {
      acc[log.uuid] = {
        reference: log.reference ?? '',
        returnTax: log.returnTax ?? '',
      };
      return acc;
    }, {});
  }, [vatReturnLogs, hideCancelled, mapping.mappingId, currentUser]);

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  };

  const renderSubmissionDate = (_, record: VatReturnLog) => {
    const result = '';
    if (DataLogStatus[record?.status] == DataLogStatus.CANCELLED) {
      return result;
    } else {
      return record?.submissionDate
        ? formatDisplayDate(record?.submissionDate, 'DD/MM/YY', i18n.language)
        : '';
    }
  };

  const getUserDisplayName = (
    user?: User | null,
    fallbackName?: string | null
  ): string | null => {
    if (user) {
      const fullName = [user.firstName, user.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (fullName) return fullName;
      if (user.email?.trim()) return user.email.trim();
    }
    const raw = fallbackName?.trim();
    return raw || null;
  };

  const getSubmitterDisplayName = (record: VatReturnLog): string | null => {
    if (DataLogStatus[record?.status] === DataLogStatus.CANCELLED) {
      return null;
    }

    const fromUser = getUserDisplayName(record.submittedByUser);
    if (fromUser) return fromUser;

    const raw = record.submittedBy?.trim();
    if (!raw) return null;

    const orgUsers = organisation?.users as User[] | undefined;
    if (orgUsers?.length) {
      const byEmail = orgUsers.find(
        (member) => member.email?.toLowerCase() === raw.toLowerCase()
      );
      const byAccount = orgUsers.find((member) => member.accountUuid === raw);
      const match = byEmail ?? byAccount;
      const resolved = getUserDisplayName(match);
      if (resolved) return resolved;
    }

    return raw;
  };

  const getPreparerDisplayName = (record: VatReturnLog): string | null => {
    if (DataLogStatus[record?.status] === DataLogStatus.CANCELLED) {
      return null;
    }

    const fromUser = getUserDisplayName(
      record.preparerUser,
      record.savedByUserFullName
    );
    if (fromUser) return fromUser;

    const raw = record.savedByUserId?.trim();
    if (!raw) return null;

    const orgUsers = organisation?.users as User[] | undefined;
    const byAccount = orgUsers?.find((member) => member.accountUuid === raw);
    return getUserDisplayName(byAccount);
  };

  const renderPwaReturnDetails = (
    record: VatReturnLog,
    action: React.ReactNode
  ) => {
    const period = renderPeriodDates(null, record);
    const submitterName = getSubmitterDisplayName(record);
    const preparerName = getPreparerDisplayName(record);
    const personLine = submitterName
      ? `${t('main.submittedBy')}: ${submitterName}`
      : preparerName
        ? `${t('main.preparer')}: ${preparerName}`
        : null;

    return (
      <div className="scope-pwa-submissions-list__body">
        <div className="scope-pwa-submissions-list__fields">
          {personLine ? (
            <span className="scope-pwa-submissions-list__meta">{personLine}</span>
          ) : null}
          {period ? (
            <span className="scope-pwa-submissions-list__period">{period}</span>
          ) : null}
        </div>
        <div className="scope-pwa-submissions-list__action">{action}</div>
      </div>
    );
  };

  const isSubmittedReturn = (record: VatReturnLog) =>
    DataLogStatus[record.status] === DataLogStatus.SUBMITTED;

  const parseReturnTaxAmount = (raw: string | undefined | null): number => {
    if (raw == null || raw === '') return NaN;
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : NaN;
  };

  const captureInitialLineValues = React.useCallback(
    (record: VatReturnLog) => {
      const draft = lineDraftById[record.uuid];
      const original = originalLineById[record.uuid] ?? {
        reference: '',
        returnTax: '',
      };
      const initial = {
        uuid: record.uuid,
        reference: original.reference,
        returnTax: original.returnTax,
      };
      editingLineInitialRef.current = initial;
      editingLineCurrentRef.current = {
        uuid: record.uuid,
        reference: draft?.reference ?? original.reference,
        returnTax: draft?.returnTax ?? original.returnTax,
      };
    },
    [lineDraftById, originalLineById]
  );

  const saveLineOnBlurIfChanged = React.useCallback(
    (record: VatReturnLog): boolean => {
      if (cancelEditOnBlurRef.current === record.uuid) {
        cancelEditOnBlurRef.current = null;
        editingLineInitialRef.current = null;
        editingLineCurrentRef.current = null;
        return true;
      }

      const draft = lineDraftById[record.uuid];
      const current = editingLineCurrentRef.current;
      const referenceValue =
        current?.uuid === record.uuid
          ? current.reference
          : draft?.reference ?? record.reference ?? '';
      const returnTaxValue =
        current?.uuid === record.uuid
          ? current.returnTax
          : draft?.returnTax ?? record.returnTax ?? '';
      const initial = editingLineInitialRef.current;
      const baseline =
        initial && initial.uuid === record.uuid
          ? initial
          : originalLineById[record.uuid] ?? {
              reference: '',
              returnTax: '',
            };

      const initialTaxParsed = parseReturnTaxAmount(baseline.returnTax);
      const currentTaxParsed = parseReturnTaxAmount(returnTaxValue);
      const hasReferenceChanged = referenceValue !== baseline.reference;
      const hasReturnTaxChanged =
        Number.isNaN(initialTaxParsed) || Number.isNaN(currentTaxParsed)
          ? returnTaxValue !== baseline.returnTax
          : initialTaxParsed !== currentTaxParsed;

      const referenceTooLong = referenceValue.length > REFERENCE_MAX_LENGTH;
      const returnTaxTooLarge =
        !Number.isNaN(currentTaxParsed) && currentTaxParsed > RETURN_TAX_MAX;
      const returnTaxTooSmall =
        !Number.isNaN(currentTaxParsed) && currentTaxParsed < RETURN_TAX_MIN;

      if (referenceTooLong || returnTaxTooLarge || returnTaxTooSmall) {
        dispatch(
          turnMessageOn({
            title: t('organisation.invalid-values'),
            description: referenceTooLong
              ? t('organisation.reference-max-characters', {
                  max: REFERENCE_MAX_LENGTH,
                })
              : returnTaxTooSmall
              ? t('organisation.return-tax-minimum', { min: RETURN_TAX_MIN })
              : t('organisation.return-tax-maximum', {
                  max: RETURN_TAX_MAX.toLocaleString(),
                }),
            type: MessageStates.WARNING,
          })
        );
        return false;
      }

      console.debug('[updateReportFields][mapping] blur check', {
        dataLogId: record.uuid,
        baseline,
        current: { reference: referenceValue, returnTax: returnTaxValue },
        hasReferenceChanged,
        hasReturnTaxChanged,
      });

      if (hasReferenceChanged || hasReturnTaxChanged) {
        if (typeof updateReportFields === 'function') {
          updateReportFields(record.uuid, referenceValue, returnTaxValue);
        } else {
          console.debug(
            '[updateReportFields][mapping] prop missing, using direct dispatch fallback'
          );
          dispatch(
            updateReportFieldsRequest(
              record.uuid,
              referenceValue,
              returnTaxValue
            )
          );
        }
      }

      editingLineInitialRef.current = null;
      editingLineCurrentRef.current = null;
      return true;
    },
    [dispatch, lineDraftById, originalLineById, updateReportFields]
  );

  const cancelRowEdit = React.useCallback(
    (record: VatReturnLog) => {
      const original = originalLineById[record.uuid] ?? {
        reference: '',
        returnTax: '',
      };
      setLineDraftById((prev) => ({
        ...prev,
        [record.uuid]: {
          reference: original.reference,
          returnTax: original.returnTax,
        },
      }));
      editingLineCurrentRef.current = {
        uuid: record.uuid,
        reference: original.reference,
        returnTax: original.returnTax,
      };
      cancelEditOnBlurRef.current = record.uuid;
    },
    [originalLineById]
  );

  const returnTaxFormatter = React.useCallback(
    (value: string | number | undefined, info?: { userTyping?: boolean }) => {
      if (value === undefined || value === null) return '';
      if (typeof value === 'string' && value === '') return '';
      const num =
        typeof value === 'number'
          ? value
          : parseFloat(String(value).replace(/,/g, ''));
      if (Number.isNaN(num)) return '';
      if (info?.userTyping) {
        return String(value);
      }
      return formatNumber(num);
    },
    [formatNumber]
  );

  const returnTaxParser = React.useCallback((display: string | undefined) => {
    if (display == null || display.trim() === '') {
      return null as unknown as number;
    }
    const normalized = display.replace(/\s/g, '').replace(/,/g, '');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? (null as unknown as number) : parsed;
  }, []);

  const renderPeriodDates = (_, record: VatReturnLog) => {
    return formatDisplayDateRange(
      record?.fromReportingDate,
      record?.toReportingDate,
      'DD/MM/YY',
      i18n.language
    );
  };

  const viewTextStyle: React.CSSProperties = {
    fontSize: 'inherit',
    color: 'inherit',
    lineHeight: 'inherit',
    width: '100%',
  };

  const renderReferenceCell = (record: VatReturnLog) => {
    const ref = record.reference ?? '';
    const canEdit = isSubmittedReturn(record) && !pwaMobileNav;
    const isEditing =
      editingLineField?.uuid === record.uuid &&
      editingLineField?.field === 'reference';

    if (!canEdit) {
      return (
        <ReferenceInputWrap>
          <Text style={viewTextStyle} ellipsis={{ tooltip: true }}>
            {ref.trim() ? ref : ''}
          </Text>
        </ReferenceInputWrap>
      );
    }

    if (isEditing) {
      return (
        <ReferenceInputWrap>
          <Input
            size="small"
            autoFocus={isEditing}
            value={ref}
            allowClear
            onFocus={() => {
              captureInitialLineValues(record);
              setEditingLineField({ uuid: record.uuid, field: 'reference' });
            }}
            onChange={(e) => {
              const value = e.target.value;
              setLineField(record.uuid, 'reference', value);
              if (editingLineCurrentRef.current?.uuid === record.uuid) {
                editingLineCurrentRef.current.reference = value;
              }
            }}
            onBlur={() => {
              const saved = saveLineOnBlurIfChanged(record);
              const isEnterSubmit =
                submitWithEnterRef.current?.uuid === record.uuid &&
                submitWithEnterRef.current?.field === 'reference';
              submitWithEnterRef.current = null;
              if (saved) {
                setEditingLineField(null);
                return;
              }
              if (isEnterSubmit) {
                requestAnimationFrame(() => {
                  setEditingLineField({
                    uuid: record.uuid,
                    field: 'reference',
                  });
                });
                return;
              }
              cancelRowEdit(record);
              setEditingLineField(null);
            }}
            onPressEnter={(e) => {
              submitWithEnterRef.current = {
                uuid: record.uuid,
                field: 'reference',
              };
              e.currentTarget.blur();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                cancelRowEdit(record);
                e.currentTarget.blur();
              }
            }}
            placeholder={t('organisation.enter-reference')}
            style={{ width: '100%', fontSize: 11 }}
          />
        </ReferenceInputWrap>
      );
    }

    return (
      <ReferenceInputWrap
        onClick={() =>
          setEditingLineField({ uuid: record.uuid, field: 'reference' })
        }
        style={{ cursor: 'pointer', minHeight: REF_RETURN_INPUT_HEIGHT_PX }}
      >
        {ref.trim() ? (
          <Text style={viewTextStyle} ellipsis={{ tooltip: true }}>
            {ref}
          </Text>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontStyle: 'italic',
              fontSize: 11,
              color: 'var(--color-placeholder, #5a6a6a)',
            }}
          >
            {t('organisation.enter-reference')}
            <StyledTooltip
              title={t('organisation.click-enter-or-edit-value')}
              placement="top"
              arrow={false}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <EditOutlined
                  style={{
                    fontSize: CELL_EDIT_PENCIL_PX,
                    color: CELL_EDIT_PENCIL_COLOR,
                  }}
                />
              </span>
            </StyledTooltip>
          </span>
        )}
      </ReferenceInputWrap>
    );
  };

  const renderReturnTaxCell = (record: VatReturnLog) => {
    const n = parseReturnTaxAmount(record.returnTax ?? '');
    const raw = String(record.returnTax ?? '').trim();
    const hasValue = raw !== '' && !Number.isNaN(n);
    const canEdit = isSubmittedReturn(record) && !pwaMobileNav;
    const isEditing =
      editingLineField?.uuid === record.uuid &&
      editingLineField?.field === 'returnTax';

    const currencyCode = record.currency ?? organisation?.currency;
    const returnTaxDisplayText = (amount: number) =>
      formatReturnTaxDisplayWithCurrency(
        currencyCode,
        numberFormatLocale,
        formatNumber(amount)
      );

    if (!canEdit) {
      return (
        <ReturnTaxNumberWrap>
          <Text
            style={{ ...viewTextStyle, textAlign: 'right' }}
            ellipsis={{ tooltip: true }}
          >
            {hasValue ? returnTaxDisplayText(n) : ''}
          </Text>
        </ReturnTaxNumberWrap>
      );
    }

    if (isEditing) {
      return (
        <ReturnTaxNumberWrap>
          {String(record.returnTax ?? '').trim() !== '' && (
            <span
              role="button"
              aria-label={t('common.clear')}
              tabIndex={0}
              onMouseDown={(e) => {
                // prevent InputNumber from losing focus before we update state
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const value = '';
                setLineField(record.uuid, 'returnTax', value);
                if (editingLineCurrentRef.current?.uuid === record.uuid) {
                  editingLineCurrentRef.current.returnTax = value;
                }
                // Save immediately so clearing persists even without blur.
                saveLineOnBlurIfChanged(record);
                setEditingLineField(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  const value = '';
                  setLineField(record.uuid, 'returnTax', value);
                  if (editingLineCurrentRef.current?.uuid === record.uuid) {
                    editingLineCurrentRef.current.returnTax = value;
                  }
                  saveLineOnBlurIfChanged(record);
                  setEditingLineField(null);
                }
              }}
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                color: 'var(--color-text-muted, #5a6a6a)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <CloseCircleFilled />
            </span>
          )}
          <InputNumber
            size="small"
            controls={false}
            autoFocus={isEditing}
            value={Number.isNaN(n) ? null : n}
            formatter={returnTaxFormatter}
            parser={returnTaxParser}
            onFocus={() => {
              captureInitialLineValues(record);
              setEditingLineField({ uuid: record.uuid, field: 'returnTax' });
            }}
            onChange={(val) => {
              const value = val != null ? String(val) : '';
              setLineField(record.uuid, 'returnTax', value);
              if (editingLineCurrentRef.current?.uuid === record.uuid) {
                editingLineCurrentRef.current.returnTax = value;
              }
            }}
            onBlur={() => {
              const saved = saveLineOnBlurIfChanged(record);
              const isEnterSubmit =
                submitWithEnterRef.current?.uuid === record.uuid &&
                submitWithEnterRef.current?.field === 'returnTax';
              submitWithEnterRef.current = null;
              if (saved) {
                setEditingLineField(null);
                return;
              }
              if (isEnterSubmit) {
                requestAnimationFrame(() => {
                  setEditingLineField({
                    uuid: record.uuid,
                    field: 'returnTax',
                  });
                });
                return;
              }
              cancelRowEdit(record);
              setEditingLineField(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submitWithEnterRef.current = {
                  uuid: record.uuid,
                  field: 'returnTax',
                };
                (e.target as HTMLElement).blur();
              } else if (e.key === 'Escape') {
                cancelRowEdit(record);
                (e.target as HTMLElement).blur();
              }
            }}
            placeholder={t('organisation.enter-value')}
            style={{ width: '100%', fontSize: 11 }}
          />
        </ReturnTaxNumberWrap>
      );
    }

    return (
      <ReturnTaxNumberWrap
        onClick={() =>
          setEditingLineField({ uuid: record.uuid, field: 'returnTax' })
        }
        style={{ cursor: 'pointer', minHeight: REF_RETURN_INPUT_HEIGHT_PX }}
      >
        {hasValue ? (
          <Text
            style={{ ...viewTextStyle, textAlign: 'right' }}
            ellipsis={{ tooltip: true }}
          >
            {returnTaxDisplayText(n)}
          </Text>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 4,
              width: '100%',
              fontStyle: 'italic',
              fontSize: 11,
              color: 'var(--color-placeholder, #5a6a6a)',
            }}
          >
            {t('organisation.enter-value')}
            <StyledTooltip
              title={t('organisation.click-enter-or-edit-value')}
              placement="top"
              arrow={false}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <EditOutlined
                  style={{
                    fontSize: CELL_EDIT_PENCIL_PX,
                    color: CELL_EDIT_PENCIL_COLOR,
                  }}
                />
              </span>
            </StyledTooltip>
          </span>
        )}
      </ReturnTaxNumberWrap>
    );
  };

  const columns = [
    {
      title: t('main.period'),
      dataIndex: 'fromReportingDate',
      key: 'fromReportingDate',
      width: '150px',
      ellipsis: true,
      render: renderPeriodDates,
      sorter: (a, b) => {
        const periodDiff =
          new Date(a.fromReportingDate).valueOf() -
          new Date(b.fromReportingDate).valueOf();
        if (periodDiff !== 0) return periodDiff;
        const activityA = new Date(
          a.lastUpdatedDate || a.generatedDate || a.submissionDate || ''
        ).valueOf();
        const activityB = new Date(
          b.lastUpdatedDate || b.generatedDate || b.submissionDate || ''
        ).valueOf();
        return (
          (Number.isNaN(activityA) ? 0 : activityA) -
          (Number.isNaN(activityB) ? 0 : activityB)
        );
      },
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.preparer'),
      dataIndex: 'savedByUserFullName',
      key: 'savedByUserFullName',
      width: 80,
      align: 'center',
      ellipsis: true,
      sorter: (a, b) =>
        a.savedByUserFullName?.localeCompare(b.savedByUserFullName),
      sortIcon: renderSortIcon,
      render: (_, record: VatReturnLog) => {
        if (
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.CLIENT
        )
          return;
        if (record.preparerUser) {
          const userId = record.preparerUser?.userUuid;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <span style={{ flexShrink: 0 }}>
                {renderAvatar(
                  record.preparerUser?.firstName,
                  record.preparerUser?.lastName,
                  AvatarSizes.SMALL,
                  userId,
                  record.preparerUser?.profilePicture
                )}
              </span>
            </div>
          );
        }
      },
    },
    {
      title: t('main.reviewer'),
      dataIndex: 'reviewerUserFullName',
      key: 'reviewerUserFullName',
      width: 80,
      align: 'center',
      ellipsis: true,
      sorter: (a, b) =>
        a.reviewerUserFullName?.localeCompare(b.reviewerUserFullName),
      sortIcon: renderSortIcon,
      render: (_, record: VatReturnLog) => {
        if (
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.CLIENT
        )
          return;
        if (record.reviewerUser) {
          const userId = record.reviewerUser.userUuid;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <span style={{ flexShrink: 0 }}>
                {renderAvatar(
                  record.reviewerUser?.firstName,
                  record.reviewerUser?.lastName,
                  AvatarSizes.SMALL,
                  userId,
                  record.reviewerUser?.profilePicture
                )}
              </span>
            </div>
          );
        }
      },
    },
    {
      title: t('main.approver'),
      dataIndex: 'approvedBy',
      key: 'approvedBy',
      width: 80,
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.approvedBy?.localeCompare(b.approvedBy),
      sortIcon: renderSortIcon,
      render: (_, record: VatReturnLog) => {
        if (record.clientUser) {
          const userId = record.clientUser.userUuid;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <span style={{ flexShrink: 0 }}>
                {renderAvatar(
                  record.clientUser.firstName,
                  record.clientUser.lastName,
                  AvatarSizes.SMALL,
                  userId,
                  record.clientUser.profilePicture
                )}
              </span>
            </div>
          );
        }
      },
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      render: (content) => (
        <StatusGetter status={DataLogStatus[content]} noDraft smaller />
      ),
      width: 140,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      sortIcon: renderSortIcon,
    },
    {
      title: t('navigation.submission'),
      dataIndex: 'submissionDate',
      key: 'submissionDate',
      align: 'left',
      width: '100px',
      render: renderSubmissionDate,
      sorter: (a, b) => {
        const aVal = a.submissionDate
          ? new Date(a.submissionDate).valueOf()
          : 0;
        const bVal = b.submissionDate
          ? new Date(b.submissionDate).valueOf()
          : 0;
        return aVal - bVal;
      },
      sortIcon: renderSortIcon,
    },
    {
      title: t('organisation.payment-reference'),
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      sorter: (a: VatReturnLog, b: VatReturnLog) =>
        (a.reference || '').localeCompare(b.reference || '', undefined, {
          sensitivity: 'base',
        }),
      sortIcon: renderSortIcon,
      render: (_: unknown, record: VatReturnLog) => renderReferenceCell(record),
    },
    {
      title: t('organisation.return-tax'),
      dataIndex: 'returnTax',
      key: 'returnTax',
      width: 110,
      align: 'right',
      sorter: (a: VatReturnLog, b: VatReturnLog) => {
        const na = parseReturnTaxAmount(a.returnTax);
        const nb = parseReturnTaxAmount(b.returnTax);
        if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
        if (Number.isNaN(na)) return 1;
        if (Number.isNaN(nb)) return -1;
        return na - nb;
      },
      sortIcon: renderSortIcon,
      showSorterTooltip: false,
      render: (_: unknown, record: VatReturnLog) => renderReturnTaxCell(record),
    },

    {
      title: t('main.action-required'),
      key: 'returnAction',
      width: '100px',
      render: (_, record: VatReturnLog) => {
        const onClick = () => {
          connectionId &&
            vatGenerate(connectionId, mapping.mappingId, record.uuid);
        };
        const actionRecord = toReturnActionRecord(record);
        return (
          mapping.mappingId && (
            <>
              <ScopeButton //@ts-ignore
                width={RETURN_ACTION_BUTTON_WIDTH_ENTITY}
                height="25px"
                fontSize="12px"
                lineheight="12px"
                onClick={onClick}
                type={
                  isReturnActionPrimary(actionRecord, currentUser)
                    ? 'primary'
                    : 'default'
                }
              >
                {getReturnActionLabel(actionRecord, t, currentUser)}
              </ScopeButton>
            </>
          )
        );
      },
    },
  ];

  const PWA_SUBMISSION_COLUMN_KEYS = new Set([
    'fromReportingDate',
    'submissionDate',
    'returnAction',
  ]);

  const tableColumns = pwaMobileNav
    ? columns.filter((col) =>
        PWA_SUBMISSION_COLUMN_KEYS.has(String(col.key ?? ''))
      )
    : columns;

  const panelStyle: React.CSSProperties = {
    marginBottom: 24,
    background: 'var(--color-surface-raised, #ffffff)',
    borderRadius: 10,
    border: '1px solid var(--color-border, #dbeae3)',
    overflow: 'hidden',
  };

  const renderExtraArea = () => {
    if (pwaMobileNav) {
      return null;
    }
    if (
      currentUser &&
      AccountType[currentUser.accountType] !== AccountType.CLIENT
    )
      return (
        <Space wrap>
          {isUkVatReturnMapping(countryName, mapping.mappingName) && (
            <>
              <HmrcConnectButton
                connected={showHmrcConnected}
                onConnect={() => {
                  const organisationId = (organisation?.uuid ?? '').trim();
                  if (!organisationId) return;
                  dispatch(
                    fetchAddUrlRequest('hmrc', organisationId, '/main', 'HMRC')
                  );
                }}
              />
              {/* <HmrcTestHeaderButton
                disabled={!showHmrcConnected}
                loading={orgLoading}
                onTest={() => {
                  const orgConnectionId = (connectionId ?? '').trim();
                  if (!orgConnectionId) return;
                  dispatch(hmrcFraudHeaderTestRequest(orgConnectionId));
                }}
              /> */}
            </>
          )}
          {currentUser &&
          AccountType[currentUser.accountType] !== AccountType.CLIENT &&
          connected &&
          mappingNoSubmissions() &&
          !hasParentGroupStartedSubmission &&
          (!hasAdvancedUserRights ||
            hasAdvancedUserRight(
              currentUser,
              organisation,
              AdvancedUserRightsCodes.CREATE_EDIT_RETURN
            )) ? (
            <StyledTooltip
              title={t('organisation.edit-tax-return-tooltip')}
              placement="top"
              arrow={false}
            >
              <ScopeButton
                type="default" //@ts-ignore
                width="185px"
                height="32px"
                style={{ fontSize: '13px', fontWeight: 700 }}
                onClick={() =>
                  connectionId &&
                  editReturnMapping(connectionId, mapping.mappingId)
                }
              >
                {t('organisation.edit-return')}
              </ScopeButton>
            </StyledTooltip>
          ) : (
            <StyledTooltip // @ts-ignore
              title={
                hasAdvancedUserRights &&
                !hasAdvancedUserRight(
                  currentUser,
                  organisation,
                  AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                )
                  ? t('global.edit-return-rights-warning')
                  : connected
                  ? hasParentGroupStartedSubmission
                    ? t(
                        'organisation.edit-tax-return-disabled-submission-tooltip'
                      )
                    : t(
                        'organisation.edit-tax-return-disabled-submission-tooltip'
                      )
                  : t(
                      'organisation.edit-tax-return-disabled-disconnected-tooltip'
                    )
              }
              placement="top"
              arrow={false}
            >
              <ScopeButton
                type="default" //@ts-ignore
                width="185px"
                height="32px"
                style={{ fontSize: '13px', fontWeight: 700 }}
                onClick={() =>
                  connectionId &&
                  editReturnMapping(connectionId, mapping.mappingId)
                }
                disabled
              >
                {t('organisation.edit-return')}
              </ScopeButton>
            </StyledTooltip>
          )}
          {isPwaTaxReturnCreationAllowed() ? (
          <StyledTooltip // @ts-ignore
            title={
              currentUser &&
              AccountType[currentUser.accountType] === AccountType.STAFF &&
              !hasAdvancedUserRight(
                currentUser,
                organisation,
                AdvancedUserRightsCodes.CREATE_SUBMISSION
              )
                ? t('global.create-submission-rights-warning')
                : ''
            }
            placement="top"
            arrow={false}
          >
            <ScopeButton
              type="primary" //@ts-ignore
              width="185px"
              height="32px"
              style={{ fontSize: '13px', fontWeight: 700 }}
              onClick={() =>
                connectionId &&
                vatGenerate(connectionId, mapping.mappingId, undefined)
              }
              disabled={
                !connected ||
                (currentUser &&
                  AccountType[currentUser.accountType] === AccountType.STAFF &&
                  !hasAdvancedUserRight(
                    currentUser,
                    organisation,
                    AdvancedUserRightsCodes.CREATE_SUBMISSION
                  ))
              }
            >
              {t('organisation.create-tax-submission')}
            </ScopeButton>
          </StyledTooltip>
          ) : null}
        </Space>
      );
  };
  const mappingNoSubmissions = () => {
    const allSubmissions = getVatLogsPerMapping();
    const startedSubmissions = allSubmissions.filter(
      (as) =>
        DataLogStatus[as.status] !== DataLogStatus.CANCELLED &&
        DataLogStatus[as.status] !== DataLogStatus.SUBMITTED
    );
    if (startedSubmissions && startedSubmissions.length > 0) {
      return false;
    } else return true;
  };
  const renderAccordionHeader = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          gap: 14,
          width: '100%',
          marginBottom:
            mapping?.dataLogs && mapping.dataLogs.length > 0 ? 15 : 0,
        }}
      >
        <div style={{ display: 'flex', marginBottom: 10 }}>
          <div
            style={{
              flex: 1,
              flexDirection: 'row',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CardTitleLargeSpan>{`${localizeCountry(countryName, countryName)} - ${getReturnTypeNameLabel(mapping.mappingName, t, i18n.language)}`}</CardTitleLargeSpan>
          </div>
          <div style={{ float: 'right' }}>{renderExtraArea()}</div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignSelf: 'center',
            flexWrap: 'wrap',
            gap: 20,
            columnGap: 50,
          }}
        >
          <CardInfo
            label={t('organisation.created-by')}
            data={
              <Text
                ellipsis={{ tooltip: mapping.createdBy }}
                style={{
                  //fontSize: 12,
                  //   padding: '2px 0',
                  //color: 'var(--color-text-muted, #5a6a6a)',
                  width: 180,
                }}
              >
                {mapping.createdBy}
              </Text>
            }
            haveBorder
          />

          <CardInfo
            label={t('organisation.created-on')}
            data={formatDisplayDate(
              mapping.createdDate,
              'DD/MM/YYYY',
              i18n.language
            )}
            haveBorder
          />

          <CardInfo
            label={t('organisation.return-frequency')}
            data={
              mapping.returnFrequency
                ? getReturnFrequencyLabel(mapping.returnFrequency, t)
                : t('common.not-available')
            }
            haveBorder
          />

          {/* <CardInfo
            label={t('organisation.return-from-date')}
            data={moment(mapping.periodFromDate).format('DD/MM/YYYY')}
            haveBorder
          /> */}
          <CardInfo
            label={t('organisation.next-period')}
            //data={mapping.nextPeriod}
            data={nextPeriod(mapping.periodFromDate, mapping.returnFrequency)}
            haveBorder
          />
          <CardInfo
            label={t('organisation.next-due-date')}
            data={
              <span
                className={
                  //@ts-ignore
                  new Date(mapping.returnDueDate)?.valueOf() <
                  new Date().valueOf()
                    ? 'expired-due-date'
                    : ''
                }
              >
                {formatDisplayDate(
                  mapping.returnDueDate,
                  'DD/MM/YYYY',
                  i18n.language
                )}
              </span>
            }
            haveBorder
          />

          <CardInfo
            label={t('organisation.cash-accounting')}
            data={
              <span>
                {mapping.cashAccounting ? t('common.yes') : t('common.no')}
              </span>
            }
            haveBorder
          />
        </div>
      </div>
    );
  };
  const nextPeriod = (selectedStartDate, selectedFrequency) => {
    return calculateNextPeriod(selectedStartDate, selectedFrequency)[0];
  };
  const savePageSize = (pageSize: number) => {
    const mappingId = mapping.mappingId;

    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_ENTITY + '-' + mappingId,
      value: pageSize,
    });
  };

  const onHiddenSwitch = () => {
    setHideCancelled(!hideCancelled);
    handlePreferences(PreferenceActions.SET, {
      name: HIDE_CANCELLED_ENTITY + '-' + mapping.mappingId,
      value: !hideCancelled,
    });
  };

  const renderPwaSubmissionsList = () => {
    const rows = getTableRows();
    const pageSize = pageSizeSaved ? Number(pageSizeSaved) : 6;
    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(pwaSubmissionsPage, totalPages);
    const start = (safePage - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    return (
      <div className="scope-pwa-submissions-table-wrap">
        <div className="scope-pwa-submissions-list" role="list">
          {pageRows.map((record) => {
            const actionRecord = toReturnActionRecord(record);
            return (
              <div
                key={record.uuid}
                className="scope-pwa-submissions-list__row"
                role="listitem"
              >
                {renderPwaReturnDetails(
                  record,
                  mapping.mappingId ? (
                    <ScopeButton
                      width={RETURN_ACTION_BUTTON_WIDTH_ENTITY}
                      height="28px"
                      fontSize="12px"
                      lineheight="12px"
                      onClick={() =>
                        connectionId &&
                        vatGenerate(
                          connectionId,
                          mapping.mappingId,
                          record.uuid
                        )
                      }
                      type={
                        isReturnActionPrimary(actionRecord, currentUser)
                          ? 'primary'
                          : 'default'
                      }
                    >
                      {getReturnActionLabel(actionRecord, t, currentUser)}
                    </ScopeButton>
                  ) : null
                )}
              </div>
            );
          })}
        </div>
        {totalPages > 1 ? (
          <div className="scope-pwa-submissions-table__footer">
            <div className="scope-pwa-submissions-table__pager">
              <ScopeButton
                type="default"
                width="auto"
                height="28px"
                fontSize="12px"
                lineheight="12px"
                disabled={safePage <= 1}
                onClick={() =>
                  setPwaSubmissionsPage((page) => Math.max(1, page - 1))
                }
              >
                {t('main.previous')}
              </ScopeButton>
              <ScopeButton
                type="default"
                width="auto"
                height="28px"
                fontSize="12px"
                lineheight="12px"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPwaSubmissionsPage((page) =>
                    Math.min(totalPages, page + 1)
                  )
                }
              >
                {t('main.next')}
              </ScopeButton>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAccordionBody = () => {
    const mappingLogs = getVatLogsPerMapping();

    return (
      <>
        {mappingLogs && mappingLogs.length > 0 && (
          <div
            className={
              pwaMobileNav ? 'scope-pwa-entity-submissions-table' : undefined
            }
            style={{
              minWidth: pwaMobileNav ? 0 : 600,
              margin: 'auto',
              marginTop: pwaMobileNav ? 0 : bodyOnly ? 16 : 0,
              borderTop:
                pwaMobileNav || mappingLogs.length === 0
                  ? 'none'
                  : '1px solid var(--color-border, #dbeae3)',
              paddingTop: pwaMobileNav ? 0 : bodyOnly ? 6 : 10,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {!pwaMobileNav &&
              mapping?.dataLogs &&
              mapping.dataLogs.length > 0 && (
              <div className="divider-20" />
            )}
            {pwaMobileNav ? (
              renderPwaSubmissionsList()
            ) : (
            <ScopeTable
              columns={tableColumns}
              mainLocaleKey="users"
              originalDataSource={getTableRows()} //@ts-ignore
              tableDataSource={getTableRows()}
              rowKey="uuid"
              pagination={true}
              rowsPerPage={pageSizeSaved ? pageSizeSaved : 6}
              enableColumnsCustomization={false}
              rowHeight={45}
              titleComponent={
                pwaMobileNav ? undefined : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <CardTitleLargeSpan
                    style={{
                      fontSize: 18,
                      marginLeft: 15,
                    }}
                  >
                    {t('main.submissions')}{' '}
                  </CardTitleLargeSpan>
                  <Space>
                    <span style={{ fontSize: 12 }}>
                      {t('organisation.hide-cancelled')}
                    </span>
                    <StyledTooltip
                      title={
                        hideCancelled
                          ? t('common.toggle-on-click-to-turn-off')
                          : t('common.toggle-off-click-to-turn-on')
                      }
                    >
                      <Switch
                        size={'small'}
                        checked={hideCancelled}
                        className={hideCancelled ? 'switch-checked' : ''}
                        aria-label={t('organisation.hide-cancelled')}
                        onChange={onHiddenSwitch}
                      />
                    </StyledTooltip>
                  </Space>
                </div>
                )
              }
              showSizeChanger={!pwaMobileNav}
              getNewPageSize={savePageSize}
              transactionType={''}
              reportId={''}
            />
            )}
          </div>
        )}
      </>
    );
  };

  const getItems: (panelStyle: CSSProperties) => CollapseProps['items'] = (
    panelStyle
  ) => [
    {
      key: '1',
      label: renderAccordionHeader(),
      children: renderAccordionBody(),
      style: panelStyle,
      //extra: renderExtraArea(),
    },
  ];

  if (bodyOnly) {
    const logs = getVatLogsPerMapping();
    if (!logs || logs.length === 0) return null;
    return <>{renderAccordionBody()}</>;
  }

  if (pwaMobileNav) {
    const logs = getVatLogsPerMapping();
    if (!logs || logs.length === 0) {
      return null;
    }

    return (
      <StyledCard width="100%" style={{ overflow: 'visible' }}>
        <div className="scope-pwa-entity-submissions-mapping">
          <CardTitleLargeSpan className="scope-pwa-entity-submissions-mapping__title">
            {`${localizeCountry(countryName, countryName)} - ${getReturnTypeNameLabel(mapping.mappingName, t, i18n.language)}`}
          </CardTitleLargeSpan>
          {renderAccordionBody()}
        </div>
      </StyledCard>
    );
  }

  return (
    <StyledCard //@ts-ignore
      //width={'fit-content'}
      width="100%"
      style={{
        //maxWidth: 1250,
        //width: '100%' /*minWidth: 916.6*/,
        overflow: 'auto',
      }}
    >
      <Collapse
        className="mapping-returns-collapse"
        collapsible="icon"
        bordered={false}
        defaultActiveKey={['1']}
        items={getItems(panelStyle)}
      />
    </StyledCard>
  );
};
